import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer'; // Import the Footer component
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectCard from './components/ProjectCard';
import ProjectFilters from './components/ProjectFilters';
import ProjectModal from './components/ProjectModal';
import MetricsDashboard from './components/MetricsDashboard';
import FeaturedProjects from './components/FeaturedProjects';

const ProjectsPortfolioUniverse = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    technology: [],
    industry: [],
    projectSize: [],
    approach: []
  });
  const [filteredProjects, setFilteredProjects] = useState([]);

  // Mock data for projects
  const projects = [
  {
    id: 1,
    title: "Interns Management System (IMS)",
    description: "A comprehensive platform to streamline the recruitment, management, and evaluation of interns for companies of all sizes.",
    fullDescription: `The Interns Management System (IMS) is a robust enterprise solution designed to tackle the complexities of running a modern internship program. It centralizes everything from applicant tracking and interview scheduling to project assignment and performance reviews.

The platform significantly reduces administrative overhead, allowing companies to focus on providing a meaningful and educational experience for their interns. It also offers a transparent and engaging experience for interns, giving them a clear view of their progress and opportunities.`,
    image: "/images/projects/ims.png",
    technologies: ["React", "TypeScript", "Node.js", "Express", "PostgreSQL", "Redis", "Docker"],
    category: "Web Application",
    industry: "Human Resources",
    projectSize: "Large",
    approach: "Agile",
    year: "2025",
    status: "Live",
    liveUrl: "https://internsms.netlify.app",
    githubUrl: null,
    metrics: [
      { value: "50%", label: "Admin Time Reduction" },
      { value: "500+", label: "Interns Managed" },
      { value: "90%", label: "Intern Satisfaction" }
    ],
    technicalApproach: [
      {
        title: "Role-based Access Control",
        description: "Implemented a granular permission system to ensure data security and proper access for administrators, mentors, and interns."
      },
      {
        title: "Automated Workflows",
        description: "Built a system for automating tasks like interview scheduling, feedback requests, and performance report generation."
      },
      {
        title: "Real-time Feedback",
        description: "Used WebSockets to provide instant feedback and communication between interns and mentors, fostering a collaborative environment."
      },
      {
        title: "Scalable Microservices",
        description: "Designed a modular microservices architecture to handle large-scale data and concurrent users without performance degradation."
      }
    ],
    keyChallenge: "Managing and integrating a complex workflow that includes application tracking, project management, and performance evaluation into a single, cohesive platform.",
    lessonsLearned: "User experience for both administrators and interns is crucial. By involving both groups in the design process, we were able to create a highly intuitive system that greatly improved the program's efficiency.",
  },
  {
    id: 2,
    title: "DigiManual",
    role: "Developer",
    description: "An exam-prep platform built for Cameroonian students — project manuals and expert mentors for GCE O-Level, A-Level, Probatoire, BAC, and Technician exams, with AI tutoring and mobile-money payments.",
    fullDescription: `DigiManual helps Cameroonian secondary school students prepare for the GCE (Ordinary & Advanced Level), Probatoire, BAC, and Technician exams. It gives students access to high-quality project manuals across subjects like ICT, Computer Science, Mathematics, Physics, Biology, and Networking, paired with an AI tutor (GPT-4o-mini) and local mobile-money payments — students pay for content access via MTN or Orange through Fapshi/MeSomb, no dollar account needed.

Rated 4.9/5 by 2,300+ students. Built as a React Native (Expo) app with a Node/Express backend on Supabase, using short-lived JWT auth and Supabase Realtime for live content sync.`,
    image: "/images/projects/dm.png",
    technologies: ["React Native (Expo)", "Node.js/Express", "Supabase", "OpenAI (GPT-4o-mini)", "Fapshi/MeSomb"],
    category: "Web Application",
    industry: "Education",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://digimanual.online",
    githubUrl: null,
    metrics: [
      { value: "4.9★", label: "Rating (2,300+ students)" }
    ],
    technicalApproach: [
      {
        title: "Subject-Structured Manuals",
        description: "Project manuals organized by exam level (OL/AL/Probatoire/BAC) and subject, built specifically for the Cameroonian curriculum."
      },
      {
        title: "AI Tutoring",
        description: "AI tutor powered by GPT-4o-mini, bundled into every plan to support students beyond the manuals themselves."
      },
      {
        title: "Mentor Marketplace",
        description: "Students can apply for and access expert mentors alongside the self-serve manual content."
      },
      {
        title: "Local Mobile-Money Payments",
        description: "Students pay for content access via MTN or Orange Mobile Money through Fapshi/MeSomb, instead of requiring a card or dollar account."
      }
    ],
    keyChallenge: "Making exam prep genuinely accessible and locally relevant for Cameroonian students, rather than adapting generic international study content.",
    lessonsLearned: "Curriculum-specific content (built around GCE/Probatoire/BAC, not a generic syllabus) is what actually drives trust with students and parents."
  },
  {
    id: 3,
    title: "TrustHaven",
    role: "Developer",
    description: "Cameroon's rental property marketplace — see the agent fee before you call, and displacement fees are banned for every verified agent.",
    fullDescription: `TrustHaven is a property-listing platform live in Cameroon that fixes two things that make house-hunting painful there: hidden agent fees and displacement fees charged just for showing up to view a property. Every listing is verified, the agent fee is shown upfront before you ever make a call, and displacement fees are banned outright for Verified Agents.

Covers the full rental lifecycle: verified listings with photos, video tours, and map coordinates, bookings, KYC-gated provider upgrades, a credit-based contact-unlock system, and review-driven trust scores. Payments run on Fapshi in production mode — real MTN MoMo / Orange Money, not sandbox — for credit packs, agent subscriptions, and featured-listing boosts.`,
    image: "/images/projects/th.png",
    technologies: ["React Native (Expo)", "Node.js/Express", "Supabase Postgres", "Cloudinary", "Twilio/Infobip", "Firebase", "Fapshi"],
    category: "Web & Mobile Application",
    industry: "Real Estate",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://gettrusthaven.com",
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "Upfront Fee Transparency",
        description: "Agent fees are shown before a prospective renter/buyer ever calls, instead of being negotiated or hidden."
      },
      {
        title: "Verified Agents & Listings",
        description: "Every listing and agent identity goes through a KYC-gated verification step before appearing on the platform."
      },
      {
        title: "No Displacement Fees",
        description: "Verified Agents are banned from charging a fee just to show up and view a property — a common practice TrustHaven was built to eliminate."
      },
      {
        title: "Five-Channel Notifications",
        description: "Email, push, SMS, in-app, and admin broadcast notifications across 18 distinct event types keep renters, agents, and admins in sync."
      },
      {
        title: "Dual Authentication",
        description: "Email OTP plus Google Sign-In today, with phone OTP built and ready pending carrier verification."
      }
    ],
    keyChallenge: "Getting agents to adopt fee transparency in a market where hidden and displacement fees were the norm.",
    lessonsLearned: "Trust has to be structural (banned fees, mandatory verification), not just a marketing claim, for a marketplace like this to earn adoption."
  },
  {
    id: 4,
    title: "Reeyo",
    role: "CTO",
    description: "A technology-enabled delivery platform connecting vendors, riders, and consumers for food, groceries, and package delivery across Cameroon, Nigeria, Kenya, and Ghana. I serve as CTO.",
    fullDescription: `Reeyo delivers food, groceries, and household essentials from restaurants, food vendors, local stores, and supermarkets to consumers in about 20 minutes, via native iOS and Android apps. Built cross-border from day one as five backend services (customer, vendor, rider, and admin APIs plus a real-time socket server) and four client apps, targeting Cameroon, Nigeria, Kenya, and Ghana.

As CTO, I lead the technical direction of the platform: the consumer app, the rider (courier) experience, merchant onboarding, and the multi-country payment and wallet infrastructure underneath all of it.`,
    image: "/images/projects/reeyo.png",
    technologies: ["Node.js 20/TypeScript", "Express", "PostgreSQL", "Redis", "AWS", "React Native (Expo)"],
    category: "Mobile Application",
    industry: "Delivery & Logistics",
    projectSize: "Large",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://usereeyo.com",
    githubUrl: null,
    metrics: [
      { value: "4.2★", label: "App Rating" },
      { value: "~20 min", label: "Avg. Delivery Time" }
    ],
    technicalApproach: [
      {
        title: "Three-Sided Marketplace",
        description: "Built and maintain the systems connecting consumers, riders, and merchants in a single coordinated delivery flow across five backend services."
      },
      {
        title: "Multi-Country Payment Routing",
        description: "Routes payments across five processors depending on market: CamPay/NotchPay (Cameroon), Paystack (Nigeria), M-Pesa (Kenya), Flutterwave (fallback), and Stripe."
      },
      {
        title: "Wallet Engine",
        description: "Custom wallet engine using Redis distributed locks for atomic credit/debit operations, plus a country-aware payout engine for riders and merchants."
      },
      {
        title: "Admin-Controlled Feature Flags",
        description: "Feature flags toggle functionality across all five services simultaneously with no redeploy needed."
      }
    ],
    keyChallenge: "Keeping delivery reliable and fast across varying network and infrastructure conditions, while coordinating three different user types and five country-specific payment rails in real time.",
    lessonsLearned: "Building for African markets means designing for intermittent connectivity, lower-end devices, and fragmented payment rails from day one, not retrofitting them later."
  },
  {
    id: 5,
    title: "STN Technologies Oy — Corporate Website",
    role: "Developer",
    description: "A fully bilingual (English/Finnish) corporate website for STN Technologies Oy, a Finland-based software development company, showcasing their services and product portfolio.",
    fullDescription: `STN Technologies Oy (registered in Espoo, Finland) builds custom software, enterprise applications, and its own proprietary products. I built their corporate website — 13 fully localized pages (English/Finnish), services overview, product showcase (ClinchDate, MyShiftMaster, ComfyBookings), GDPR-compliant cookie consent, and a spam-protected lead-generation contact flow.

Beyond the website, I do server-side and mobile app development work across STN's product line — see ClinchDate, MyShiftMaster, and ComfyBookings below.`,
    image: "/images/projects/stn.png",
    technologies: ["Next.js 13 (App Router)", "TypeScript", "Tailwind", "next-intl", "Resend", "Vercel"],
    category: "Marketing Website",
    industry: "Client Services",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://stntechglobal.com",
    githubUrl: null,
    metrics: [
      { value: "13", label: "Localized Pages" },
      { value: "2", label: "Languages (EN/FI)" }
    ],
    technicalApproach: [
      {
        title: "Services & Product Showcase",
        description: "Built dedicated sections for STN's service offerings and its in-house product suite."
      },
      {
        title: "Full Internationalization",
        description: "Locale-aware routing and SEO (JSON-LD, sitemap, robots.txt) across English and Finnish via next-intl."
      },
      {
        title: "Lead Generation",
        description: "A spam-protected, GDPR-compliant contact and inquiry flow aimed at converting visitors into client conversations."
      }
    ],
    keyChallenge: "Representing both a services business and a multi-product company on a single, coherent, bilingual site.",
    lessonsLearned: "Clear separation between 'what we build for you' and 'what we've built ourselves' matters a lot for a dev-shop-plus-products company like STN."
  },
  {
    id: 6,
    title: "ClinchDate",
    role: "Developer",
    description: "A dating and relationship platform (iOS & Android, 6 languages) built for genuine connections, with profile verification, smart matching, structured video dates, and encrypted messaging.",
    fullDescription: `ClinchDate is STN Technologies' live dating product — profile verification, intelligent matching, in-app messaging, structured 7-minute video "Mini Dates," AI-powered icebreakers, and a safety-first design with emergency contacts and a reputation-score system. Per ClinchDate's own published figures: 500,000+ members across 120 countries and 80,000+ couples matched.

I work on the server-side and mobile app development for the product, including a published shared SDK (\`@clinchdate/api-client\`) that unifies auth, token refresh, and error handling across the mobile app, web, and admin — a real multi-frontend architecture, not just a mobile app.`,
    image: "/images/projects/clinchdate.png",
    technologies: ["React Native (Expo)", "Node.js/Express/TypeScript", "MongoDB", "Socket.IO", "Twilio", "Firebase"],
    category: "Mobile Application",
    industry: "Dating & Relationships",
    projectSize: "Large",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://clinchdate.com",
    githubUrl: null,
    metrics: [
      { value: "500K+", label: "Members (120 countries)" },
      { value: "80K+", label: "Couples Matched" },
      { value: "4.9★", label: "App Store Rating" }
    ],
    technicalApproach: [
      {
        title: "Verified Profiles",
        description: "Phone and email verification plus active moderation to keep fake profiles and bots out."
      },
      {
        title: "Smart Matching",
        description: "Matching driven by preferences, location, and in-app behavior rather than simple proximity."
      },
      {
        title: "Real-Time Infrastructure",
        description: "Socket.IO for chat/calls/presence, Twilio for video/voice Mini Dates, Firebase for push and crash analytics."
      },
      {
        title: "Published Shared SDK",
        description: "A published \`@clinchdate/api-client\` package unifying auth, token refresh, and error handling across mobile, web, and admin."
      }
    ],
    keyChallenge: "Keeping the platform safe and authentic at scale — verification and moderation have to work continuously as membership grows across 120 countries.",
    lessonsLearned: "Trust and safety features aren't a layer on top of a dating product — they're the product."
  },
  {
    id: 7,
    title: "Schedulr (MyShiftMaster)",
    role: "Developer",
    description: "A multi-tenant workforce-scheduling platform for retail, hospitality, and healthcare teams — smart scheduling, conflict detection, and mobile time-tracking. Deliberately exports timesheets rather than processing payroll itself.",
    fullDescription: `MyShiftMaster lets managers build conflict-free schedules while staff set availability, swap shifts, and clock in/out from their phones, with automatic conflict detection and geofenced attendance tracking. Per MyShiftMaster's own published figures: 98% fewer scheduling conflicts, 500+ teams scheduling weekly, and 10,000+ shifts published monthly.

I work on the server-side and mobile app development for the product. Backend and mobile app are functionally complete; the web console and admin panel are still in progress.`,
    image: "/images/projects/myshiftmaster.png",
    technologies: ["Node.js/Express/TypeScript", "Supabase", "React Native (Expo)", "Vite/React", "Next.js"],
    category: "Web & Mobile Application",
    industry: "Workforce Management",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "In Development",
    liveUrl: "https://myshiftmaster.com",
    githubUrl: null,
    metrics: [
      { value: "98%", label: "Fewer Scheduling Conflicts" },
      { value: "500+", label: "Teams Scheduling Weekly" },
      { value: "10,000+", label: "Shifts Published Monthly" }
    ],
    technicalApproach: [
      {
        title: "Two-Tier Conflict Engine",
        description: "Hard-blocks double-booking outright, and soft-blocks-with-override for availability conflicts, before anything gets published."
      },
      {
        title: "Swaps & Time Off",
        description: "Employees request swaps and time off from their phone; requests land in an approval queue with availability already checked."
      },
      {
        title: "Geofenced Time Clock",
        description: "Mobile clock-ins with geofence flags, automatic absence detection, and exportable timesheets plus labor-cost estimation."
      },
      {
        title: "Multi-Tenant Isolation",
        description: "Full data isolation per tenant, with passwordless OTP-only authentication and invite-based team onboarding."
      }
    ],
    keyChallenge: "Handling recurring availability, swaps, and time-off requests without ever double-booking a shift, across multiple locations and tenants.",
    lessonsLearned: "For hourly-workforce tools, trust comes from the schedule never being wrong — conflict detection has to be airtight before anything else."
  },
  {
    id: 8,
    title: "ComfyBookings",
    role: "Developer",
    description: "Cameroon's two-sided booking marketplace — hotels, vacation rentals, home care visits, airport transfers, and car rentals, all in one place, with real Stripe Connect payments and payouts.",
    fullDescription: `ComfyBookings covers hotels, vacation rentals, serviced apartments, assisted-living and disability-friendly stays, home-care and nursing visits, airport transfers, and car rentals — with verified listings, discovery, real-time availability, and verified hosts. Live at STN Technologies, still ramping up listings and host onboarding.

I work on the server-side and mobile app development for the product, deployed as four independently deployable projects (Railway backend, Vercel web/admin, EAS mobile) each with its own CI pipeline.`,
    image: "/images/projects/comfybookings.png",
    technologies: ["Node.js/Express/TypeScript", "React Native (Expo)", "Next.js 14", "Supabase (Postgres/PostGIS)", "Stripe Connect"],
    category: "Web & Mobile Application",
    industry: "Hospitality & Care Services",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://comfybookings.com",
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "Verified Listings & Hosts",
        description: "Host and provider verification for stays, transport, and care services listed on the platform."
      },
      {
        title: "PostGIS Location Search",
        description: "Location-based discovery with a real-time availability calendar across all 10 booking categories, backed by PostGIS."
      },
      {
        title: "Real Stripe Connect Payments",
        description: "Stripe Connect payments and payouts, verified end-to-end against production in a documented pre-launch audit — not sandbox."
      },
      {
        title: "Cameroon-First Auth",
        description: "Custom phone-OTP authentication built for the local market, alongside a four-project deploy architecture (Railway/Vercel/EAS)."
      }
    ],
    keyChallenge: "Modeling very different booking types — short-term stays vs. recurring home-care visits vs. car rentals — inside one consistent availability and booking system.",
    lessonsLearned: "In progress — early stage, still onboarding hosts and providers at STN."
  },
  {
    id: 9,
    title: "Skilldera",
    role: "Founder",
    description: "Lets African creators sell courses, ebooks, and digital products and get paid the next morning, straight to MTN or Orange Mobile Money — no dollar account, no FX loss.",
    fullDescription: `Skilldera is a CodersHub Innovations project solving a specific problem: existing platforms like Selar, Gumroad, and Teachable either pay out slowly, require a PayPal account most Cameroonian creators don't have, or price everything in USD. Skilldera pays creators directly in XAF via MTN or Orange Mobile Money, the payment method nearly every Cameroonian already uses. Includes affiliate links, subscription plans, and live feature-flagged rollouts.`,
    image: "/images/projects/skilldera.png",
    technologies: ["Next.js 15 (App Router)", "Supabase", "Fapshi", "Resend", "Anthropic/Google AI SDKs"],
    category: "Web Application",
    industry: "Creator Economy",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://skilldera.co",
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "Mobile Money Payouts",
        description: "Creators get paid directly to MTN or Orange Mobile Money, next-morning, instead of waiting days for a bank/PayPal payout."
      },
      {
        title: "XAF-Native Pricing",
        description: "Products are priced and sold in XAF, removing the FX conversion friction of USD-priced platforms."
      },
      {
        title: "Hardened Payment Pipeline",
        description: "Webhook payloads are never trusted at face value — the server re-verifies status directly with Fapshi and runs all money movement through atomic Postgres RPCs to prevent lost-update races."
      },
      {
        title: "Runtime Feature Flags",
        description: "Functionality toggles live from an admin panel with zero redeploys, including affiliate links and subscription plans."
      }
    ],
    keyChallenge: "Building reliable, direct integration with MTN and Orange Mobile Money payout rails instead of relying on international payment processors.",
    lessonsLearned: "In progress — details to be expanded as the platform matures."
  },
  {
    id: 10,
    title: "IMAC Prepa",
    description: "Website for Impact Academy (IMAC), a concours-prep school in Buea training students for ENS, ENAM, ENSET, FMSB/Medical, Engineering School, and Military School entrance exams.",
    fullDescription: `Impact Academy (IMAC) is a concours-prep school founded in August 2023 in the Bonadikombo community in Buea, Cameroon, now operating across three centers (Bonadikombo, Half Mile, Isokolo). It's trained 700+ students with a 95%+ overall success rate in Cameroon's most competitive entrance examinations. I built their website — program listings (ENS, ENAM, ENSET, FMSB/Medical, Engineering Schools, Military Schools), enrollment flow, and results/success-rate showcase. I'm also involved with IMAC on the mentorship side, developing structured learning paths for students.`,
    image: "/images/projects/imac.png",
    technologies: ["Web Development"],
    category: "Web Application",
    industry: "Education",
    projectSize: "Small",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://imacprepa.org",
    githubUrl: null,
    metrics: [
      { value: "700+", label: "Students Trained" },
      { value: "95%+", label: "Success Rate" },
      { value: "3", label: "Centers in Buea" }
    ],
    technicalApproach: [
      {
        title: "Program Showcase",
        description: "Dedicated pages for each concours track (ENS, ENAM, ENSET, FMSB/Medical, Engineering Schools, Military Schools) with subject breakdowns, duration, and per-program success rates."
      },
      {
        title: "Enrollment Flow",
        description: "Enrollment and advisory-request flow so prospective students can sign up or ask for guidance on which program fits them."
      },
      {
        title: "Results & Track Record",
        description: "Site-wide stats (students trained, success rate, years active) pulled from IMAC's real cohort outcomes rather than placeholder numbers."
      }
    ],
    keyChallenge: "Presenting six different exam-prep tracks with distinct requirements and success rates in a way that's easy for prospective students to compare and choose from.",
    lessonsLearned: "Concrete, program-specific success rates (78%+ for ENS, 88%+ for ENSET, etc.) build more trust with parents and students than a single blended statistic."
  },
  {
    id: 11,
    title: "CodersHub Innovations",
    role: "Founder",
    description: "An integrated tech ecosystem serving Cameroon and West Africa through eight specialized divisions under one platform — education, housing, talent, incubation, research, design, community, and operations.",
    fullDescription: `CodersHub Innovations runs eight arms under one platform: DevCore (LMS with courses/certificates), Skilldera (creator commerce), HubWorks (internship/job placement), HubLabs (startup incubator), Techspire (research), DesignHub (UI/UX), HubConnect (community/events), and OpsDesk (internal operations). All arms share one MongoDB Atlas database, a unified Firebase-authenticated API gateway, and role-based access control — effectively a Notion-meets-LinkedIn-meets-Udemy back office for running an entire tech ecosystem.

Alongside the platform engineering, I founded and still run the CodersHub developer community itself — grown from a small WhatsApp group into 500+ members, with workshops and mentorship programs that have helped members land their first tech jobs.`,
    image: "/images/projects/codershub-innovations.png",
    technologies: ["Node.js/Express", "Python/FastAPI", "React", "TypeScript", "MongoDB Atlas", "Firebase Auth", "n8n"],
    category: "Web Application",
    industry: "SaaS / Platform",
    projectSize: "Large",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://codershublabs.com",
    githubUrl: null,
    metrics: [
      { value: "8", label: "Divisions" },
      { value: "63+", label: "Data Models" },
      { value: "500+", label: "Community Members" }
    ],
    technicalApproach: [
      {
        title: "Dual-Backend Architecture",
        description: "Node/Express (63+ models, 43+ controllers across 12 modules) plus a companion Python/FastAPI service for AI resume scoring (GPT-4o-mini) and PDF certificate generation with QR codes."
      },
      {
        title: "8-Level Role-Based Access",
        description: "Per-division access control from super_admin down to community_member, shared across all eight arms."
      },
      {
        title: "Workflow Automation",
        description: "n8n wired into backend events via webhooks to automate cross-division workflows."
      },
      {
        title: "Community Built From Zero",
        description: "Founded and grew the CodersHub community from a WhatsApp group to 500+ members, with workshops and mentorship that have led to real job placements."
      }
    ],
    keyChallenge: "Building one coherent platform (shared auth, database, and access control) underneath eight functionally very different divisions, instead of eight disconnected products.",
    lessonsLearned: "A single shared API gateway and RBAC system across every division made adding new divisions dramatically cheaper than building each as a silo."
  },
  {
    id: 12,
    title: "DevCore",
    role: "Founder",
    description: "CodersHub's developer training platform — an LMS combining coding missions with real code execution and grading, an AI mentor, portfolio hosting, and a job marketplace.",
    fullDescription: `DevCore is CodersHub's developer training platform: an LMS combining coding missions, an AI mentor, portfolio hosting, and a job marketplace. The interactive coding-mission flow is fully functional end-to-end — write code, run it, get graded — with skills/badges, quizzes, leaderboards, and job boards scoped for later phases.`,
    image: "/images/projects/devcore.png",
    technologies: ["Next.js", "TypeScript", "Supabase (Postgres + Auth)", "Radix UI", "Monaco Editor", "Express", "Piston API"],
    category: "Web Application",
    industry: "Education",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "In Development",
    liveUrl: null,
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "Code-Execution Grading Pipeline",
        description: "Submissions run against hidden test cases via the Piston API, with expected outputs stripped from published payloads so answers can't be inspected client-side."
      },
      {
        title: "Row-Level Security by Design",
        description: "Supabase Postgres with Row-Level Security across profiles, learning paths, and submissions, plus an auto-provisioning trigger for new user profiles."
      }
    ],
    keyChallenge: "Building a real code-execution and grading pipeline (not just multiple-choice quizzes) that's both secure against answer-peeking and fast enough to feel interactive.",
    lessonsLearned: "In development — frontend is deployed and the mission flow works end-to-end; a dedicated backend for skills/badges/job boards is the next phase."
  },
  {
    id: 13,
    title: "OfferBid",
    role: "Cofounder & Engineer",
    description: "A hyper-local second-hand marketplace for Buea, Cameroon, built to replace chaotic WhatsApp/Facebook trading groups with structured, real-time offers.",
    fullDescription: `OfferBid is a hyper-local second-hand marketplace for Buea, Cameroon (expanding to Lagos, Nigeria). Buyers submit structured offers instead of chat spam; sellers accept, reject, or counter in real time; accepted deals hand off to WhatsApp for the actual transaction.`,
    image: "/images/projects/offerbid.png",
    technologies: ["NestJS", "Prisma", "PostgreSQL", "Redis", "BullMQ", "Socket.IO", "AWS S3", "React Native (Expo)", "Next.js"],
    category: "Web & Mobile Application",
    industry: "Marketplace",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://offerbid-api.onrender.com",
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "Real-Time Counter-Bidding",
        description: "A counter-bidding engine over Socket.IO, backed by BullMQ for 24-hour offer expiry and scheduled digest notifications."
      },
      {
        title: "Presigned S3 Direct Upload",
        description: "The API never touches image bytes — the app compresses and uploads straight to S3/CloudFront via presigned URLs."
      },
      {
        title: "Tiered Seller Trust & KYC",
        description: "A tiered seller trust system with a dedicated KYC identity-verification pipeline."
      }
    ],
    keyChallenge: "Replacing the low-friction habit of WhatsApp/Facebook trading groups with a structured flow, without losing the speed people are used to.",
    lessonsLearned: "The API is live; web and mobile are built and pending public launch — real-time offer state has to feel instant or people fall back to chat groups."
  },
  {
    id: 14,
    title: "ADFX",
    role: "Developer",
    description: "A trading intelligence and education platform for retail forex traders — charting, risk management, a trade journal, backtesting, and an AI trade-setup assistant. Not a brokerage.",
    fullDescription: `ADFX is deliberately an analysis and education product, not a brokerage — no order execution or custody of funds at any tier by permanent design. Combines charting, a risk-management engine, a trade journal with performance analytics, a backtesting lab, an AI trade-setup assistant, and a 15-module structured trading curriculum.`,
    image: "/images/projects/adfx.png",
    technologies: ["Vite", "React 18", "TypeScript", "Tailwind", "Supabase", "Claude API", "Stripe", "Fapshi"],
    category: "Web Application",
    industry: "Trading & Finance",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "Live",
    liveUrl: "https://adfx-ch.vercel.app/",
    githubUrl: null,
    metrics: [
      { value: "15", label: "Curriculum Modules" }
    ],
    technicalApproach: [
      {
        title: "AI Trade Assistant",
        description: "Wired to the real Claude API for structured, risk-warned trade-setup feedback — not canned responses."
      },
      {
        title: "Dual Payment Rails",
        description: "Stripe globally, Fapshi mobile money in Cameroon."
      },
      {
        title: "No Custody, By Design",
        description: "No order execution or custody of funds at any tier — the product is deliberately scoped to analysis and education only."
      }
    ],
    keyChallenge: "Building genuinely useful trading tools (journal, backtesting, AI feedback) while staying strictly out of brokerage/custody territory by design.",
    lessonsLearned: "Roadmap includes a computer-vision pipeline for analyzing uploaded chart screenshots directly."
  },
  {
    id: 15,
    title: "Radiograph3D",
    role: "Developer",
    description: "A medical-imaging platform (Cameroon pilot) that converts 2D radiographs into interactive 3D anatomical models using AI, as a clinical decision-support tool.",
    fullDescription: `Radiograph3D is positioned as a clinical decision-support tool for radiologists and surgeons, not a diagnostic device. Planned as a full multi-app system: web dashboard, offline-capable desktop app, tablet app, and a HoloLens/AR surgical-visualization surface. Currently in the architecture and planning stage — not yet implemented.`,
    image: "/images/projects/radiograph3d.png",
    technologies: ["FastAPI", "Celery", "PyTorch", "Orthanc (DICOM)", "React Native", "Electron", "Unity/MRTK 3"],
    category: "Web & Mobile Application",
    industry: "Healthcare & MedTech",
    projectSize: "Large",
    approach: "Agile",
    year: "Planning",
    status: "In Development",
    liveUrl: null,
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "2D-to-3D AI Pipeline",
        description: "A custom AI pipeline (X2CT-GAN) converting a single 2D X-ray into a 3D CT volume, targeting sub-30-second cloud inference with an offline CPU fallback."
      },
      {
        title: "DICOM Compliance & Privacy",
        description: "Full DICOM compliance with patient-privacy design: PII stripped before cloud upload, AES-256 encryption at rest."
      },
      {
        title: "AR Surgical Visualization",
        description: "A planned HoloLens/AR surgical-visualization surface — something most portfolios never attempt."
      }
    ],
    keyChallenge: "Designing a clinically credible decision-support tool (not a diagnostic device) that still needs real accuracy and patient-privacy guarantees before it can be piloted.",
    lessonsLearned: "Architecture and planning stage — implementation hasn't started yet; documented here honestly as a planned system, not a shipped one."
  },
  {
    id: 16,
    title: "KelFlow",
    role: "Developer",
    description: "A cold-outreach CRM built for dev agencies and freelancers who close deals over email and WhatsApp.",
    fullDescription: `KelFlow lets you import leads, write rich templates with auto-follow-up sequences, run multi-channel campaigns, and track call bookings and no-show rates with funnel analytics — built specifically for dev agencies and freelancers who close deals over email and WhatsApp rather than a generic sales CRM.`,
    image: "/images/projects/kelflow.png",
    technologies: ["Next.js 14", "Supabase", "TipTap", "Resend", "Meta WhatsApp Cloud API", "Python"],
    category: "Web Application",
    industry: "Developer Tools",
    projectSize: "Medium",
    approach: "Agile",
    year: "Ongoing",
    status: "In Development",
    liveUrl: null,
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "True Multi-Channel Outreach",
        description: "Email and WhatsApp Cloud API unified into one campaign and follow-up engine."
      },
      {
        title: "Companion Lead Scraper",
        description: "A Python lead-scraper service feeding directly into the CRM pipeline."
      },
      {
        title: "Multi-Tenant by Row-Level Security",
        description: "Multi-tenant data access enforced through Supabase Row-Level Security."
      }
    ],
    keyChallenge: "Unifying email and WhatsApp into one follow-up engine without either channel feeling bolted on.",
    lessonsLearned: "In development — details to be expanded as the platform matures."
  },
  {
    id: 17,
    title: "DevLead",
    role: "Developer",
    description: "A B2B SaaS concept combining a client-acquisition engine with an AI-assisted project-scoping and proposal generator.",
    fullDescription: `DevLead combines a client-acquisition engine (scraping and enriching leads from Google Maps, LinkedIn, Instagram, and Facebook, then automating personalized outreach) with a project-delivery engine — an AI assistant that turns a client's idea into a full scope, recommended tech stack, and a generated proposal. Currently in the planning/architecture stage.`,
    image: "/images/projects/devlead.png",
    technologies: ["Python", "N8N", "React", "Google Gemini (planned)"],
    category: "Web Application",
    industry: "Developer Tools",
    projectSize: "Small",
    approach: "Agile",
    year: "Planning",
    status: "In Development",
    liveUrl: null,
    githubUrl: null,
    metrics: [],
    technicalApproach: [
      {
        title: "Dual-Sided Value Proposition",
        description: "Lead generation feeding directly into AI-assisted project scoping for the same user."
      },
      {
        title: "Planned AI Scoping Engine",
        description: "A scoping engine generating buildable prompt files a developer can hand directly to an AI coding assistant."
      }
    ],
    keyChallenge: "Connecting lead generation and project scoping into one coherent flow instead of two separate tools.",
    lessonsLearned: "Planning/architecture stage — implementation hasn't started; documented honestly as a concept, not a shipped product."
  }
];

  // Portfolio metrics — display-only summary; does NOT drive `featuredProjects` below (that's always projects[0..2])
  const portfolioMetrics = [
    {
      icon: "FolderOpen",
      value: "17+",
      label: "Featured Projects",
      description: "Across web & mobile"
    },
    {
      icon: "Users",
      value: "500K+",
      label: "Users Reached",
      description: "Via ClinchDate"
    },
    {
      icon: "Code",
      value: "25+",
      label: "Technologies Used",
      description: "Across the stack"
    },
    {
      icon: "Globe",
      value: "2",
      label: "Countries",
      description: "Cameroon & Finland"
    }
  ];

  // Filter configuration
  const filterOptions = {
    technology: ["React", "React Native", "TypeScript", "Node.js", "Next.js", "Elixir", "Backend Development", "Mobile App Development", "Web Development"],
    industry: ["Human Resources", "Education", "Real Estate", "Delivery & Logistics", "Client Services", "Dating & Relationships", "Workforce Management", "Hospitality & Care Services", "Creator Economy", "SaaS / Platform", "Marketplace", "Trading & Finance", "Healthcare & MedTech", "Developer Tools"],
    projectSize: ["Small", "Medium", "Large"],
    approach: ["Agile", "Scrum", "Waterfall"]
  };

  // Featured projects (first 3 projects)
  const featuredProjects = projects?.slice(0, 3);

  // Filter projects based on active filters
  useEffect(() => {
    let filtered = projects;

    Object.entries(activeFilters)?.forEach(([category, values]) => {
      if (values?.length > 0) {
        filtered = filtered?.filter(project => {
          if (category === 'technology') {
            return values?.some(value => project?.technologies?.includes(value));
          }
          return values?.includes(project?.[category]);
        });
      }
    });

    setFilteredProjects(filtered);
  }, [activeFilters, projects]);

  const handleFilterChange = (category, values) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: values
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      technology: [],
      industry: [],
      projectSize: [],
      approach: []
    });
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <>
      <Helmet>
        <title>Ebesoh Adrian - Portfolio Universe | Adrian - Fullstack Developer & Mentor</title>
        <meta name="description" content="Explore Adrian's comprehensive project portfolio showcasing React and React Native expertise across healthcare, finance, education, and e-commerce industries. Detailed case studies with technical insights." />
        <meta name="keywords" content="React projects, React Native apps, portfolio, case studies, web development, mobile development, TypeScript, Node.js" />
        <meta property="og:title" content="Projects - Portfolio Universe | Adrian" />
        <meta property="og:description" content="Comprehensive project portfolio showcasing technical expertise and problem-solving methodology across diverse industries." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/projectspage" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-3 mb-6">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Icon name="FolderOpen" size={32} className="text-primary" />
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-brand-headline text-foreground">
                  Portfolio Universe
                </h1>
              </div>
              <p className="text-xl text-text-secondary font-brand-body max-w-3xl mx-auto leading-relaxed">
                Explore comprehensive case studies showcasing technical expertise, problem-solving methodology, and measurable business impact across diverse industries and project scales.
              </p>
            </div>

            {/* Metrics Dashboard */}
            <MetricsDashboard metrics={portfolioMetrics} />
          </div>
        </section>

        <hr className="my-8 max-w-7xl mx-auto border-t border-muted-foreground/10" />

        {/* Featured Projects Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <FeaturedProjects
              projects={featuredProjects}
              onViewDetails={handleViewDetails}
            />
          </div>
        </section>

        <hr className="my-8 max-w-7xl mx-auto border-t border-muted-foreground/10" />

        {/* All Projects Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-brand-headline text-foreground mb-4">
                All Projects
              </h2>
              <p className="text-text-secondary font-brand-body max-w-2xl mx-auto">
                Browse the complete portfolio with advanced filtering options to find projects by technology, industry, or development approach.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Filters Sidebar - Now appears on top on smaller screens */}
              <div className="lg:col-span-1">
                <div className="lg:sticky lg:top-24">
                  <ProjectFilters
                    filters={filterOptions}
                    activeFilters={activeFilters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    projectCount={filteredProjects?.length}
                  />
                </div>
              </div>

              {/* Projects Grid */}
              <div className="lg:col-span-3">
                {filteredProjects?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {filteredProjects?.map((project) => (
                      <ProjectCard
                        key={project?.id}
                        project={project}
                        onViewDetails={handleViewDetails}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="p-4 bg-muted rounded-xl inline-block mb-4">
                      <Icon name="Search" size={48} className="text-text-secondary" />
                    </div>
                    <h3 className="text-xl font-brand-subheading text-foreground mb-2">
                      No Projects Found
                    </h3>
                    <p className="text-text-secondary font-brand-body mb-6">
                      Try adjusting your filters to see more projects.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleClearFilters}
                      iconName="RotateCcw"
                      iconPosition="left"
                      iconSize={18}
                    >
                      Clear All Filters
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <hr className="my-8 max-w-7xl mx-auto border-t border-muted-foreground/10" />

        {/* Footer */}
        <Footer />

        {/* Project Detail Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </div>
    </>
  );
};

export default ProjectsPortfolioUniverse;